import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  ArrowLeft, Camera, Loader2, Save, LogOut, User, Mail, Phone, Target, Bell, BellOff, Fingerprint, Trash2, Shield
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { isPlatformAuthenticatorAvailable, registerBiometric, listCredentials, removeCredential } from "@/utils/webauthn";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Profile() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [postcode, setPostcode] = useState(user?.postcode || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { isSupported, isSubscribed, permission, loading: pushLoading, subscribe, unsubscribe } = usePushNotifications();
  
  // Biometric state
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    isPlatformAuthenticatorAvailable().then(setBiometricSupported);
    loadCredentials();
  }, []);

  // Sync local state when user data loads/changes
  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setPhone(user.phone || "");
      setPostcode(user.postcode || "");
    }
  }, [user]);


  const loadCredentials = async () => {
    const creds = await listCredentials();
    setCredentials(creds);
  };

  const handleEnrolBiometric = async () => {
    setEnrolling(true);
    try {
      await registerBiometric("Biometric Login");
      toast.success("Biometric login enabled!");
      loadCredentials();
    } catch (err) {
      if (err.name === "NotAllowedError") {
        toast.error("Biometric enrolment was cancelled");
      } else if (err.name === "InvalidStateError") {
        toast.error("This device is already enrolled");
      } else {
        toast.error(err.message || "Failed to enable biometric login");
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handleRemoveCredential = async (credId) => {
    const ok = await removeCredential(credId);
    if (ok) {
      toast.success("Biometric credential removed");
      loadCredentials();
    } else {
      toast.error("Failed to remove credential");
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Name cannot be empty");
      return;
    }

    setSaving(true);

    try {
      // Get session token from localStorage for iOS Safari fallback
      const storedToken = localStorage.getItem('session_token');
      const headers = { "Content-Type": "application/json" };
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
      
      const response = await fetch(`${API}/profile`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ name, phone: phone || null, postcode: postcode || null })
      });

      if (!response.ok) {
        throw new Error("Failed to update profile");
      }

      await checkAuth(); // Refresh user data
      
      // Haptic feedback for success
      if (navigator.vibrate) {
        navigator.vibrate([20, 30, 50]);
      }
      toast.success("Profile updated!");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      // Get session token from localStorage for iOS Safari fallback
      const storedToken = localStorage.getItem('session_token');
      const headers = {};
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }

      const response = await fetch(`${API}/profile/image`, {
        method: "POST",
        headers,
        body: formData
      });

      if (!response.ok) {
        throw new Error("Failed to upload image");
      }

      await checkAuth(); // Refresh user data
      toast.success("Profile image updated!");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";
  };

  const getProfileImageUrl = () => {
    if (!user?.picture) return null;
    // If it's a relative URL (uploaded), prepend the API URL
    if (user.picture.startsWith("/api")) {
      return process.env.REACT_APP_BACKEND_URL + user.picture;
    }
    return user.picture;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center gap-4 px-4 py-3 lg:px-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="h-10 md:h-8" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 md:w-4 md:h-4 mr-1 md:mr-2" />
              <span className="hidden md:inline">Back</span>
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 md:w-6 md:h-6 text-primary" />
            <span className="font-heading text-lg md:text-xl font-bold uppercase">Profile</span>
          </div>
          <Link to="/" className="md:hidden ml-auto" data-testid="profile-header-logo-link">
            <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8" />
          </Link>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-2xl mx-auto">
        {/* Profile Image Card */}
        <Card className="border-border bg-card mb-4 md:mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center">
              <div className="relative mb-4">
                <Avatar className="w-28 h-28 md:w-32 md:h-32 border-4 border-primary/50">
                  <AvatarImage src={getProfileImageUrl()} alt={user?.name} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-2xl md:text-3xl font-heading">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <Button
                  size="sm"
                  className="absolute bottom-0 right-0 rounded-full w-11 h-11 md:w-10 md:h-10 p-0 bg-primary hover:bg-primary/90"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="change-avatar-btn"
                >
                  {uploading ? (
                    <Loader2 className="w-5 h-5 md:w-4 md:h-4 animate-spin" />
                  ) : (
                    <Camera className="w-5 h-5 md:w-4 md:h-4 text-primary-foreground" />
                  )}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>
              <h2 className="font-heading text-xl md:text-2xl uppercase">{user?.name}</h2>
              <p className="text-muted-foreground text-sm">{user?.email}</p>
            </div>
          </CardContent>
        </Card>

        {/* Edit Profile Card */}
        <Card className="border-border bg-card mb-4 md:mb-6">
          <CardHeader className="pb-2 md:pb-4">
            <CardTitle className="font-heading uppercase text-xl md:text-2xl flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Edit Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 bg-background border-input h-14 md:h-10"
                  data-testid="name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  value={user?.email || ""}
                  className="pl-10 bg-background border-input h-14 md:h-10"
                  disabled
                  data-testid="email-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">Email cannot be changed</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g., +1 555-123-4567"
                  className="pl-10 bg-background border-input h-14 md:h-10"
                  data-testid="phone-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">Visible to police if your tool is reported stolen</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="postcode">Postcode</Label>
              <div className="relative">
                <Target className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="postcode"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                  placeholder="e.g., SW1A 1AA"
                  className="pl-10 bg-background border-input h-14 md:h-10"
                  data-testid="postcode-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">Police use only: used to identify theft hotspots</p>
            </div>

            <Button 
              className="w-full btn-primary h-14 md:h-10 text-base md:text-sm" 
              onClick={handleSave}
              disabled={saving || (name === user?.name && phone === (user?.phone || "") && postcode === (user?.postcode || ""))}
              data-testid="save-profile-btn"
            >
              {saving ? (
                <><Loader2 className="w-5 h-5 md:w-4 md:h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-5 h-5 md:w-4 md:h-4 mr-2" /> Save Changes</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Push Notifications Card */}
        <Card className="border-border bg-card mb-4 md:mb-6">
          <CardHeader className="pb-2 md:pb-4">
            <CardTitle className="font-heading uppercase text-xl md:text-2xl flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              Push Notifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!isSupported ? (
              <p className="text-sm text-muted-foreground">Push notifications are not supported on this browser.</p>
            ) : permission === "denied" ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Notifications are blocked. Please enable them in your browser settings to receive alerts.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 pr-4">
                    <p className="text-sm font-medium">
                      {isSubscribed ? "Notifications enabled" : "Enable notifications"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {isSubscribed 
                        ? "You'll receive alerts when your tools are scanned and when thefts occur near you."
                        : "Get instant alerts when your stolen tools are scanned and when tool thieves are active in your area."
                      }
                    </p>
                  </div>
                  <Button
                    variant={isSubscribed ? "outline" : "default"}
                    size="sm"
                    onClick={async () => {
                      if (isSubscribed) {
                        await unsubscribe();
                        toast.success("Push notifications disabled");
                      } else {
                        const success = await subscribe();
                        if (success) {
                          toast.success("Push notifications enabled!");
                        } else if (permission === "denied") {
                          toast.error("Notifications blocked. Check browser settings.");
                        }
                      }
                    }}
                    disabled={pushLoading}
                    className="flex-shrink-0"
                    data-testid="push-notification-toggle"
                  >
                    {pushLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isSubscribed ? (
                      <><BellOff className="w-4 h-4 mr-2" /> Disable</>
                    ) : (
                      <><Bell className="w-4 h-4 mr-2" /> Enable</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Biometric Login */}
        {biometricSupported && (
          <Card className="border-border bg-card">
            <CardHeader className="pb-2 md:pb-4">
              <CardTitle className="font-heading uppercase text-xl md:text-2xl flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Biometric Login
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Use your device's fingerprint or face recognition for quick, secure login without typing your password.
              </p>

              {credentials.length > 0 ? (
                <div className="space-y-2">
                  {credentials.map((cred) => (
                    <div key={cred.credential_id} className="flex items-center justify-between p-3 bg-background rounded-lg border border-border">
                      <div className="flex items-center gap-3">
                        <Fingerprint className="w-5 h-5 text-primary" />
                        <div>
                          <p className="text-sm font-medium">{cred.name || "Biometric Login"}</p>
                          <p className="text-xs text-muted-foreground">
                            Added {new Date(cred.created_at).toLocaleDateString()}
                            {cred.last_used && ` · Last used ${new Date(cred.last_used).toLocaleDateString()}`}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleRemoveCredential(cred.credential_id)}
                        data-testid="remove-biometric-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleEnrolBiometric}
                    disabled={enrolling}
                    data-testid="add-another-biometric-btn"
                  >
                    {enrolling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Fingerprint className="w-4 h-4 mr-2" />}
                    Add Another Device
                  </Button>
                </div>
              ) : (
                <Button
                  className="w-full btn-primary"
                  onClick={handleEnrolBiometric}
                  disabled={enrolling}
                  data-testid="enable-biometric-btn"
                >
                  {enrolling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Fingerprint className="w-4 h-4 mr-2" />}
                  Enable Biometric Login
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Account Actions */}
        <Card className="border-border bg-card">
          <CardHeader className="pb-2 md:pb-4">
            <CardTitle className="font-heading uppercase text-xl md:text-2xl">Account</CardTitle>
          </CardHeader>
          <CardContent>
            <Button 
              variant="outline" 
              className="w-full h-14 md:h-10 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 md:w-4 md:h-4 mr-2" />
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
